import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "../../hooks/hooks";
import Leaderboard from "./Leaderboard";
import ScrollableSection from "./ScrollableSection";
import { CardContainer } from "./CardContainer";
import { SectionContainer } from "./SectionContainer";
import { Header } from "./Header";
import { clearSearch } from "../../features/store/appDataSlice";
const categories = [
  {
    key: "most_popular",
    title: "Most Popular Today",
    icon: "/png/MostPopular.png",
  },
  {
    key: "topup",
    title: "Direct Game Topup",
    icon: "/png/DirectGameTopUp.png",
  },
  {
    key: "game_cards",
    title: "Game Cards",
    icon: "/png/GameCards.png",
  },
  {
    key: "gift_cards",
    title: "Gift Cards",
    icon: "/png/GiftCards.png",
  },
  {
    key: "dg_coins",
    title: "DG Coins",
    icon: "/png/dg.png",
  },
  { key: "data", title: "Data Services", icon: "/png/PrepaidServices.png" },
  {
    key: "airtime",
    title: "Airtime Services",
    icon: "/png/PrepaidServices.png",
  },
  { key: "cable", title: "Cable TV", icon: "/png/CableTV.png" },
  {
    key: "electric",
    title: "Electric Payment",
    icon: "/png/ElectricityPayment.png",
  },
];

const Main = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { data: appData } = useAppSelector((state) => state.appData);
  const selectedCountry = useAppSelector((state) => state.locale.selectedCountry);
  
  const [isScrolled, setIsScrolled] = useState(false);

  // Filter providers based on selected country
  const filterProvidersByCountry = useMemo(() => {
    if (!appData || !selectedCountry?.id) return appData;

    const filteredData = { ...appData };

    // Helper function to filter providers for a specific category key
    const filterProviders = (categoryKey: string) => {
      const category = appData[categoryKey];
      if (!category?.providers) return category;

      const filteredProviders = category.providers.filter((provider: any) => {
        // Get categories for this provider
        const providerCategoryIds = appData.data?.categories
          ?.filter((cat: any) => cat.provider_id === provider.id)
          ?.map((cat: any) => cat.id) || [];

        // Check if any plan matches both category and country
        const hasMatchingCountry = appData.data?.plans?.some((plan: any) => 
          providerCategoryIds.includes(plan.category_id) && 
          plan.countries?.includes(selectedCountry.id)
        );

        return hasMatchingCountry;
      });

      return {
        ...category,
        providers: filteredProviders,
      };
    };

    // Apply filtering to all categories
    categories.forEach(({ key }) => {
      filteredData[key] = filterProviders(key);
    });

    return filteredData;
  }, [appData, selectedCountry?.id]);

  useEffect(() => {
    if (location.state?.scrollTo && !isScrolled) {
      const scrollToSection = () => {
        const sectionId = `section-${location.state.scrollTo}`;
        const sectionElement = document.getElementById(sectionId);

        if (sectionElement) {
          sectionElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          setIsScrolled(true);
          return true;
        }
        return false;
      };

      // Try immediately
      if (!scrollToSection()) {
        // If not found, try again after a short delay (components might still be rendering)
        const timer = setTimeout(() => {
          scrollToSection();
        }, 300);

        return () => clearTimeout(timer);
      }
    }
  }, [location.state, isScrolled, appData]); // Add appData to dependencies

  // Reset scroll state when location changes
  useEffect(() => {
    setIsScrolled(false);
  }, [location.state]);
  return (
    <>
      {/* Desktop View */}
      <div className="hidden bp:block bp:col-14 bp:pt-10 bp:pb-2 min-w-[300px]">
        {categories.map(({ key, title, icon }) => {
          const category = filterProvidersByCountry?.[key];
          const providers = category?.providers;
          return (
            providers && providers.length > 0 && (
              <ScrollableSection
                key={key}
                providers={providers}
                icon={icon}
                title={title}
                viewAllUrl={key}
              />
            )
          );
        })}
      </div>

      {/* Mobile View */}
      <div className="block bp:hidden col-6 px-4 pt-10 pb-2">
       {categories.map(({ key, title, icon }) => {
          const category = filterProvidersByCountry?.[key];
          const providers = category?.providers;
          return (
            providers && providers.length > 0 && (
              <SectionContainer key={key} id={`section-${key}`}>
                <Header icon={icon} title={title} viewAllUrl={key} />
                <CardContainer cardData={providers} url={key} />
              </SectionContainer>
            )
          );
        })}

        <Leaderboard />
      </div>
    </>
  );
};

export default Main;
